import React, { memo, useState } from 'react';
import { NodeProps, Position } from 'reactflow';
import { ChangeInitiativeStatus } from '@components/MainStage/CustomNode/ChangeInitiativeStatus';
import { KeyControlsImpact } from '@components/MainStage/CustomNode/KeyControlsImpact';
import { KeyControlsImpactColor } from '@components/MainStage/CustomNode/KeyControlsImpact/helpers';
import { RiskScore } from '@components/MainStage/CustomNode/RiskScore';
import {
  getHoverState,
  useIsNodeHovered,
} from '@components/MainStage/hover.store';
import { useSearchNeedle } from '@components/MainStage/listSearch.store';
import { useIsNodeMultiSelected } from '@components/MainStage/multiSelection.store';
import { InitiativeProgressStatus } from '@constants/canvas/layers';
import { useNodesDataContext } from '@context/NodesDataContext';
import { Typography } from '@mui/material';
import { NodeDTOTypes } from '@store/services/nodes/types';
import { ImpactType } from '@store/services/projects/types';
import { themePalette } from '@theme/muiTheme';
import { getInitiativeNodeIcon, getNodeIcon } from '@utils/canvasIconHelpers';
import { renderHighlightedText } from '@utils/searchHighlight';
import { Tabs } from '@views/Insights/types';

import {
  BottomHandleWrapper,
  CustomNodeContent,
  CustomNodeStyled,
  CustomNodeToolbar,
  EvolutionMaturityBlock,
  EvolutionMaturityWrapper,
  IconWrapper,
  IncompleteLabel,
  MaturityBlock,
  MaturityWrapper,
  StyledHandle,
  StyledText,
  StyledTooltipLeftArrow,
  TextWrapper,
  TopHandleWrapper,
} from './CustomNode.styled';

let hoverDelay: ReturnType<typeof setTimeout>;

export const CustomNode = memo(function CustomNode({
  id,
  data,
  selected,
}: NodeProps) {
  const {
    getIsNodeTargetConnected,
    getIsNodeSourceConnected,
    getActiveNode,
    getActiveEdge,
    isConnectable,
    isNodeDisabled,
    insightsTab,
  } = useNodesDataContext();

  const [isHovered, setIsHovered] = useState(false);
  const isMultiSelected = useIsNodeMultiSelected(id);
  const isNodeHovered = useIsNodeHovered(id);
  const searchNeedle = useSearchNeedle();

  const activeNode = getActiveNode();
  const activeEdge = getActiveEdge();

  const targetSelectedWithEdge = id === activeEdge?.target;
  const sourceSelectedWithEdge = id === activeEdge?.source;
  const { type, is_complete: isComplete, label, meta_data } = data.dto;
  const {
    selectedWithEdge,
    selectedInInsight,
    selectedInMaturity,
    maturityBackground,
    keyControlImpact,
    disabled,
  } = data.canvas;

  const isIncomplete = !isComplete;
  const isDisabled = isNodeDisabled(data.canvas) && !isNodeHovered;
  const isNodeTargetHidden = type === NodeDTOTypes.StrategicPriority;

  const isNodeTargetConnected = getIsNodeTargetConnected(id);
  const isNodeSourceConnected = getIsNodeSourceConnected(id);

  const nodeIcon =
    type === NodeDTOTypes.Initiatives
      ? getInitiativeNodeIcon({
          type: meta_data?.type,
          disabled: isDisabled,
        })
      : getNodeIcon({ type, disabled: isDisabled });

  const handleMouseEnter = () => {
    getHoverState().setHovered(id);
    hoverDelay = setTimeout(() => {
      setIsHovered(true);
    }, 1000);
  };

  const handleMouseLeave = () => {
    getHoverState().clear();
    clearTimeout(hoverDelay);
    setIsHovered(false);
  };

  const isSelectedInInsight =
    insightsTab === Tabs.Dependency &&
    selectedInInsight &&
    !activeNode &&
    !selectedWithEdge &&
    !activeEdge?.id;

  const shouldBeSelectedInMaturity =
    selectedInMaturity &&
    meta_data?.current_maturity &&
    meta_data?.target_maturity &&
    isComplete;

  const isInitiativeStatusShown =
    type === NodeDTOTypes.Initiatives &&
    (meta_data?.progress_status === InitiativeProgressStatus.Completed ||
      meta_data?.progress_status === InitiativeProgressStatus.Cancelled);

  const isKeyControlImpactShown =
    selectedInInsight && keyControlImpact && !disabled;
  const keyControlImpactColor =
    KeyControlsImpactColor[keyControlImpact as ImpactType];

  // eslint-disable-next-line no-nested-ternary
  const evolutionStatues = data?.diff
    ? data.diff > 0
      ? 'positive'
      : 'negative'
    : undefined;

  return (
    <CustomNodeStyled
      $selected={selected}
      $hovered={isNodeHovered}
      $multiSelected={isMultiSelected}
      $incomplete={isIncomplete}
      $selectedWithEdge={selectedWithEdge && activeEdge?.id}
      $selectedInInsight={isSelectedInInsight}
      $selectedInMaturity={shouldBeSelectedInMaturity}
      $maturityBackground={maturityBackground}
      $keyControlsImpactColor={keyControlImpactColor}
      $disabled={isDisabled}
      $evolution={evolutionStatues}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isIncomplete && (
        <IncompleteLabel $disabled={isDisabled}>
          <Typography
            variant="body2"
            color={isDisabled ? 'grey.700' : 'system.error'}
          >
            Incomplete
          </Typography>
        </IncompleteLabel>
      )}

      {shouldBeSelectedInMaturity && (
        <MaturityWrapper>
          <MaturityBlock
            $maturityBackground={maturityBackground}
            $isDisabled={disabled}
          >
            {meta_data?.current_maturity}
          </MaturityBlock>
          <MaturityBlock $isDisabled={disabled}>
            {meta_data?.target_maturity}
          </MaturityBlock>
        </MaturityWrapper>
      )}

      {evolutionStatues && (
        <EvolutionMaturityWrapper>
          <EvolutionMaturityBlock $evolution={evolutionStatues}>
            {data.diff.toString().length === 1
              ? data.diff.toString()
              : data.diff.toFixed(1)}
          </EvolutionMaturityBlock>
        </EvolutionMaturityWrapper>
      )}

      {type === NodeDTOTypes.EvaluatedRisk && <RiskScore data={data} />}
      {isInitiativeStatusShown && (
        <ChangeInitiativeStatus status={meta_data.progress_status} />
      )}
      {isKeyControlImpactShown && (
        <KeyControlsImpact impact={keyControlImpact} />
      )}
      <CustomNodeToolbar
        isVisible={label.length > 30 && isHovered}
        position={Position.Right}
      >
        <StyledTooltipLeftArrow />
        <Typography
          variant="body2"
          color="white"
          sx={{ wordBreak: 'break-word' }}
        >
          {label}
        </Typography>
      </CustomNodeToolbar>
      <CustomNodeContent>
        <IconWrapper>{nodeIcon}</IconWrapper>
        <TextWrapper>
          <StyledText
            variant="h5"
            color={isDisabled ? 'grey.700' : 'grey.1000'}
          >
            {renderHighlightedText(
              data?.dto?.label ?? '',
              searchNeedle,
              themePalette.yellow[800],
            )}
          </StyledText>
        </TextWrapper>
      </CustomNodeContent>
      {!isNodeTargetHidden && (
        <TopHandleWrapper $isHandleConnected={isNodeTargetConnected}>
          <StyledHandle
            type="target"
            position={Position.Top}
            $selectedWithEdge={targetSelectedWithEdge}
            isConnectable={isConnectable}
            $faded={isDisabled}
          />
        </TopHandleWrapper>
      )}
      <BottomHandleWrapper $isHandleConnected={isNodeSourceConnected}>
        <StyledHandle
          type="source"
          position={Position.Bottom}
          $selectedWithEdge={sourceSelectedWithEdge}
          isConnectable={isConnectable}
          $faded={isDisabled}
        />
      </BottomHandleWrapper>
    </CustomNodeStyled>
  );
});
